import {
  useGlobalUndoRedo,
  type ActionData,
} from "@/composables/useUndoRedoWithStorage";

export function initializeUndoRedo(getStore: () => any) {
  const { setExecuteAction } = useGlobalUndoRedo();

  const executeAction = async (action: ActionData, isUndo: boolean) => {
    const store = getStore();

    switch (action.type) {
      case "cluster_rename":
        if (
          action.payload.clusterId &&
          action.payload.oldName &&
          action.payload.newName
        ) {
          const targetName = isUndo
            ? action.payload.oldName
            : action.payload.newName;
          await store.renameCluster(action.payload.clusterId, targetName, true);
        }
        break;

      case "cluster_move":
        if (action.payload.clusterId !== undefined) {
          const targetParentId = isUndo
            ? action.payload.oldParentId
            : action.payload.newParentId;
          // true здесь предназначался для skipHistory, но попадал в position
          // и уходил на сервер как position=true — валидация ждёт
          // before/after/inside, поэтому отмена перемещения падала с 422.
          await store.moveCluster(
            action.payload.clusterId,
            targetParentId ?? null,
            undefined,
            undefined,
            true,
          );
        }
        break;

      case "keywords_move":
        if (
          action.payload.keywordIds &&
          action.payload.targetClusterId !== undefined
        ) {
          // Если targetClusterId === -1, это означает перемещение в корзину
          if (action.payload.targetClusterId === -1) {
            if (isUndo) {
              // Отмена удаления - восстанавливаем ключевые слова
              console.log("[initUndoRedo] UNDO delete - restoring keywords", {
                total: action.payload.keywordIds.length,
              });
              if (action.payload.keywordIds.length > 0) {
                await store.restoreKeywords(action.payload.keywordIds, true);
              }
            } else {
              // Повтор удаления - удаляем ключевые слова
              console.log("[initUndoRedo] REDO delete - removing keywords", {
                total: action.payload.keywordIds.length,
              });
              if (action.payload.keywordIds.length > 0) {
                await store.removeKeywords(action.payload.keywordIds, true);
              }
            }
          } else {
            // Обычное перемещение между кластерами
            if (isUndo) {
              // Отмена перемещения - возвращаем ключевые слова в исходный кластер
              if (action.payload.sourceClusterId !== undefined) {
                await store.moveKeywordsToCluster(
                  action.payload.keywordIds,
                  action.payload.sourceClusterId,
                  action.payload.targetClusterId,
                );
              }
            } else {
              // Повтор перемещения - перемещаем ключевые слова в целевой кластер
              await store.moveKeywordsToCluster(
                action.payload.keywordIds,
                action.payload.targetClusterId,
                action.payload.sourceClusterId,
              );
            }
          }
        }
        break;

      case "keywords_collect":
        if (action.payload.keywordIds && action.payload.keywordIds.length > 0) {
          if (isUndo) {
            // Undo collect — remove the collected keywords
            console.log(
              "[initUndoRedo] UNDO keywords_collect - removing keywords",
              {
                total: action.payload.keywordIds.length,
              },
            );
            await store.removeKeywords(action.payload.keywordIds, true);
          } else {
            // Redo collect — restore the collected keywords
            console.log(
              "[initUndoRedo] REDO keywords_collect - restoring keywords",
              {
                total: action.payload.keywordIds.length,
              },
            );
            await store.restoreKeywords(action.payload.keywordIds, true);
          }
        }
        break;

      case "cluster_delete":
        if (action.payload.clusterId && action.payload.clusterName) {
          if (isUndo) {
            // Undo delete — recreate the cluster with its keywords
            console.log(
              "[initUndoRedo] UNDO cluster_delete - restoring cluster",
              {
                name: action.payload.clusterName,
                keywordIds: action.payload.keywordIds?.length || 0,
              },
            );
            await store.createManualCluster(
              action.payload.clusterName,
              action.payload.keywordIds || [],
              action.payload.oldParentId || null,
              true,
            );
          } else {
            // Redo delete — delete the cluster again (find by name since ID changed)
            console.log(
              "[initUndoRedo] REDO cluster_delete - deleting cluster again",
              {
                name: action.payload.clusterName,
              },
            );
            const cluster = store.clusters.find(
              (c: any) => c.name === action.payload.clusterName,
            );
            if (cluster) {
              await store.deleteCluster(cluster.id, true);
            }
          }
        }
        break;

      default:
        console.warn("Unknown action type:", action.type);
    }
  };

  setExecuteAction(executeAction);
}
